import { createFileRoute } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { UnitCard } from "@/components/UnitCard";
import { ActionLink } from "@/components/ActionLink";
import { BRAND, UNITS } from "@/data/units";
import { openTrialDialog } from "@/components/TrialDialog";
import { track } from "@/lib/tracking";

const TITLE = "Unidades Nook | Academias de treino personalizado no Rio";
const DESCRIPTION =
  "As três unidades Nook no Rio: Recreio — Le Gusta, Barra da Tijuca e Recreio — Américas. Treino personalizado com até três alunos por instrutor.";

export const Route = createFileRoute("/unidades/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://nookgym.com.br/unidades" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://nookgym.com.br/unidades" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbSchema([
            { name: "Início", url: `${BRAND.site}/` },
            { name: "Unidades", url: `${BRAND.site}/unidades` },
          ]),
        ),
      },
    ],
  }),
  component: UnitsPage,
});

function UnitsPage() {
  return (
    <>
      <section className="relative overflow-hidden border-b border-border pb-16 pt-28">
        <img
          src={UNITS[1].images[0].src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Unidades" }]} />
          <span className="eyebrow mt-10 block">Unidades</span>
          <h1 className="display mt-5 max-w-3xl text-[2.75rem] sm:text-6xl">
            Onde a Nook acontece.
          </h1>
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-white/75">
            Três studios no Rio de Janeiro, o mesmo método: treino personalizado, até três alunos por
            instrutor e acompanhamento do início ao fim da sessão.
          </p>
        </div>
      </section>

      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {UNITS.map((u) => (
              <UnitCard key={u.slug} unit={u} showAddress showPlans={false} plansLabel="Ver planos" />
            ))}
          </div>

          <ul className="mt-14 grid gap-px overflow-hidden rounded-sm border border-border bg-border md:grid-cols-3">
            {UNITS.map((u) => (
              <li key={u.slug} className="bg-background p-7">
                <h2 className="text-sm font-semibold uppercase tracking-[0.12em]">{u.shortName}</h2>
                <p className="mt-3 flex gap-2 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-nook-green" aria-hidden="true" />
                  {u.addressLine}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <a
                    href={u.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => track("click_maps", { unit: u.slug })}
                    className="inline-block text-xs uppercase tracking-[0.16em] text-nook-green"
                  >
                    Ver no mapa
                  </a>
                  <a
                    href={u.wazeUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => track("click_waze", { unit: u.slug })}
                    className="inline-block text-xs uppercase tracking-[0.16em] text-nook-green"
                  >
                    Abrir no Waze
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="relative overflow-hidden bg-offwhite py-20 text-offwhite-foreground">
        <img
          src={UNITS[0].images[0].src}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply"
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="display max-w-2xl text-4xl sm:text-5xl">
            Escolha a unidade
            <br className="hidden sm:block" />
            e venha treinar.
          </h2>
          <div className="mt-9">
            <ActionLink
              variant="green"
              onClick={() => {
                track("click_experimental", { where: "unidades_cta_final" });
                openTrialDialog("unidades_cta_final");
              }}
            >
              Garanta seu Day Use →
            </ActionLink>
          </div>
        </div>
      </section>
    </>
  );
}
